import { useCallback, useRef, type MutableRefObject } from 'react';

import type { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { resolveGridColumnFieldId } from '@resistapp/client/utils/ag-grid-sheet-utils';
import { SheetPatchField, isSheetPatchField } from '@resistapp/common/api-types';
import {
  formatIsoDate,
  formatSampleTypeLabel,
  formatSamplingPointSubTypeLabel,
  parseCoordinatesCell,
  parseDateCell,
  toSampleType,
} from '@resistapp/common/sample-sheet';
import {
  type CellRange,
  type Column,
  type GridApi,
  type GridOptions,
  type PasteEndEvent,
  type PasteStartEvent,
  type ProcessCellForExportParams,
  type ProcessDataFromClipboardParams,
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { isEqual } from 'lodash';

import { cloneRow, replaceRowValue, type SampleSheetRowDraft } from '../sample-sheet-data';
import { normalizeClipboardValue } from './normalize-clipboard';

export type ResolveMode =
  | { kind: 'manualOrSingleField'; allowByKey: true }
  | { kind: 'multiCellPaste'; allowByKey: boolean };

type QueuePatchOp = {
  rowId: string;
  field: SheetPatchField;
  value: unknown;
  previousValue?: unknown;
};

type PasteContext = {
  blockedCells: number;
  blockedSamplingPointCells: number;
  blockedReadOnlyCells: number;
  affectedRowIds: Set<string>;
  blockedSamplingPointPasteAction: BlockedSamplingPointPasteAction;
  clipboardRowCount: number;
  mutatedSamplingFieldsByRow: Map<string, Set<keyof SampleSheetRowDraft>>;
  mutatedCells: Array<{ rowId: string; field: keyof SampleSheetRowDraft; value: unknown }>;
  pastedCellCount: number;
  beforeRows: Map<string, SampleSheetRowDraft>;
};

export type UseSmartchipClipboardArgs = {
  gridRef: MutableRefObject<AgGridReact<SampleSheetRowDraft> | null>;
  samplingPointFieldSet: ReadonlySet<keyof SampleSheetRowDraft>;
  resolveSamplingPointRef: MutableRefObject<(row: SampleSheetRowDraft, mode: ResolveMode) => SampleSheetRowDraft>;
  beginSubmittedSamplingPointEdit?: (args: {
    api: GridApi<SampleSheetRowDraft>;
    rowId: string;
    rowIndex: number | null | undefined;
    colId: string;
    field: keyof SampleSheetRowDraft;
    eventKey?: string;
  }) => Promise<void>;
  showToast: ReturnType<typeof useToastWithoutDebounce>;
  countryNameByAlpha3: Record<string, string>;
  snapDropdownValue: (
    field: keyof SampleSheetRowDraft,
    raw: string,
    row: SampleSheetRowDraft,
    options?: { suppressCanonicalSelect?: boolean },
  ) => string;
  rowsByIdRef: MutableRefObject<Map<string, SampleSheetRowDraft>>;
  queuePatch: (ops: QueuePatchOp[]) => void;
};

function formatCoordinatesForClipboard(value: unknown): string {
  const parsed = parseCoordinatesCell(value);
  if (parsed === undefined) {
    return value == null ? '' : String(value);
  }
  if (!parsed) {
    return '';
  }
  return `${parsed.lat}, ${parsed.lon}`;
}

function getRowDataById(api: GridApi<SampleSheetRowDraft>, rowId: string): SampleSheetRowDraft | null {
  let found: SampleSheetRowDraft | null = null;
  api.forEachNode(node => {
    if (node.data?.rowId === rowId) {
      found = node.data;
    }
  });
  return found;
}

function toQueuePatchField(field: keyof SampleSheetRowDraft): SheetPatchField | null {
  return isSheetPatchField(field) ? field : null;
}

function preservePastedFieldValues(
  resolvedRow: SampleSheetRowDraft,
  baseRow: SampleSheetRowDraft,
  fieldMap: Map<keyof SampleSheetRowDraft, unknown>,
): SampleSheetRowDraft {
  if (fieldMap.size === 0 || resolvedRow === baseRow) {
    return resolvedRow;
  }
  let workingRow = resolvedRow;
  fieldMap.forEach((value, fieldKey) => {
    const typedField = fieldKey as keyof SampleSheetRowDraft;
    const desiredValue = value as SampleSheetRowDraft[typeof typedField];
    if (isEqual(workingRow[typedField], desiredValue)) {
      return;
    }
    if (workingRow === resolvedRow) {
      workingRow = cloneRow(resolvedRow);
    }
    workingRow = replaceRowValue(workingRow, typedField, desiredValue);
  });
  return workingRow;
}

type BlockedSamplingPointPasteAction =
  | { kind: 'none' }
  | {
      kind: 'modal';
      target: { rowId: string; rowIndex: number; colId: string; field: keyof SampleSheetRowDraft };
    }
  | { kind: 'toast'; title: string; description: string };

export function useSmartchipClipboard(args: UseSmartchipClipboardArgs) {
  const {
    gridRef,
    samplingPointFieldSet,
    resolveSamplingPointRef,
    beginSubmittedSamplingPointEdit,
    showToast,
    countryNameByAlpha3,
    snapDropdownValue,
    rowsByIdRef,
    queuePatch,
  } = args;

  const pasteContextRef = useRef<PasteContext>({
    blockedCells: 0,
    blockedSamplingPointCells: 0,
    blockedReadOnlyCells: 0,
    affectedRowIds: new Set<string>(),
    blockedSamplingPointPasteAction: { kind: 'none' },
    clipboardRowCount: 0,
    mutatedSamplingFieldsByRow: new Map<string, Set<keyof SampleSheetRowDraft>>(),
    mutatedCells: [],
    pastedCellCount: 0,
    beforeRows: new Map<string, SampleSheetRowDraft>(),
  });

  const processDataFromClipboard = useCallback((clipboardParams: ProcessDataFromClipboardParams) => {
    const { data } = clipboardParams;
    if (!Array.isArray(data) || data.length === 0) {
      return data;
    }
    const normalized = data.map(row => row.map((value: unknown) => normalizeClipboardValue(value)));
    pasteContextRef.current.clipboardRowCount = normalized.length;
    const pasteAction = pasteContextRef.current.blockedSamplingPointPasteAction;
    if (pasteAction.kind === 'modal' && normalized.length > 1) {
      pasteContextRef.current.blockedSamplingPointPasteAction = {
        kind: 'toast',
        title: 'Paste not supported for shared sampling points',
        description:
          'This paste includes sampling point fields for submitted samples. Pasting across multiple rows is not supported when the sampling point is shared. Edit one row at a time (double click a sampling point cell to clear first).',
      };
    }
    return normalized;
  }, []);

  const processCellForClipboard = useCallback(
    (cellParams: ProcessCellForExportParams<SampleSheetRowDraft>) => {
      const columnId = cellParams.column.getColId();
      if (columnId === 'latitudeLongitude') {
        return formatCoordinatesForClipboard(cellParams.value);
      }
      if (columnId === 'sampleCollectionDate') {
        const parsedDate = parseDateCell(cellParams.value);
        return parsedDate ? formatIsoDate(parsedDate) : '';
      }
      if (columnId === 'country') {
        const raw: unknown = cellParams.value;
        if (typeof raw !== 'string') {
          return raw == null ? '' : String(raw);
        }
        const trimmed = raw.trim();
        if (trimmed === '') {
          return '';
        }
        const upper = trimmed.toUpperCase();
        if (Object.prototype.hasOwnProperty.call(countryNameByAlpha3, upper)) {
          return countryNameByAlpha3[upper] ?? trimmed;
        }
        return trimmed;
      }
      if (columnId === 'sampleType') {
        const label = formatSampleTypeLabel(cellParams.value);
        return label || (cellParams.value == null ? '' : String(cellParams.value));
      }
      if (columnId === 'detailedSampleType') {
        const row = cellParams.node?.data;
        const sampleType = row ? toSampleType(row.sampleType) : null;
        const label = formatSamplingPointSubTypeLabel(cellParams.value, sampleType);
        return label || (cellParams.value == null ? '' : String(cellParams.value));
      }
      if (columnId === 'sampleDescription') {
        const row = cellParams.node?.data;
        return row?.sampleDescription ?? '';
      }
      const baseValue: unknown = cellParams.value;
      if (typeof baseValue === 'string' || typeof baseValue === 'number' || typeof baseValue === 'boolean') {
        return String(baseValue);
      }
      return baseValue == null ? '' : String(baseValue);
    },
    [countryNameByAlpha3],
  );

  const processCellFromClipboard = useCallback(
    (clipboardParams: ProcessCellForExportParams<SampleSheetRowDraft>): unknown => {
      const columnFieldFromDef = clipboardParams.column.getColDef().field;
      const columnField =
        typeof columnFieldFromDef === 'string' && columnFieldFromDef.length > 0
          ? columnFieldFromDef
          : clipboardParams.column.getColId();

      const rowData = clipboardParams.node?.data ?? undefined;
      const rowId = rowData?.rowId;
      if (rowId) {
        pasteContextRef.current.affectedRowIds.add(rowId);
      }
      if (!rowData || !columnField) {
        return clipboardParams.value;
      }

      const typedField = columnField as keyof SampleSheetRowDraft;
      const isSamplingPointField = samplingPointFieldSet.has(typedField);

      const pasteAction = pasteContextRef.current.blockedSamplingPointPasteAction;
      if (pasteAction.kind !== 'none') {
        pasteContextRef.current.blockedCells += 1;
        if (isSamplingPointField) {
          pasteContextRef.current.blockedSamplingPointCells += 1;
        } else {
          pasteContextRef.current.blockedReadOnlyCells += 1;
        }
        return rowData[typedField] ?? null;
      }

      if (rowData.isReadOnly) {
        pasteContextRef.current.blockedCells += 1;
        pasteContextRef.current.blockedReadOnlyCells += 1;
        return rowData[typedField] ?? null;
      }

      const editableResult = clipboardParams.node
        ? clipboardParams.column.isCellEditable(clipboardParams.node)
        : undefined;
      const isEditable = editableResult ?? true;
      if (!isEditable) {
        pasteContextRef.current.blockedCells += 1;
        if (isSamplingPointField) {
          pasteContextRef.current.blockedSamplingPointCells += 1;
        } else {
          pasteContextRef.current.blockedReadOnlyCells += 1;
        }
        return rowData[typedField] ?? null;
      }

      if (rowId && isSamplingPointField) {
        let mutatedFields = pasteContextRef.current.mutatedSamplingFieldsByRow.get(rowId);
        if (!mutatedFields) {
          mutatedFields = new Set<keyof SampleSheetRowDraft>();
          pasteContextRef.current.mutatedSamplingFieldsByRow.set(rowId, mutatedFields);
        }
        mutatedFields.add(typedField);
      }

      pasteContextRef.current.pastedCellCount += 1;

      const baseValue: unknown = clipboardParams.value;
      const normalized =
        typeof baseValue === 'string'
          ? snapDropdownValue(typedField, baseValue, rowData, { suppressCanonicalSelect: isSamplingPointField })
          : baseValue;

      if (rowId) {
        pasteContextRef.current.mutatedCells.push({ rowId, field: typedField, value: normalized });
      }
      return normalized;
    },
    [samplingPointFieldSet, snapDropdownValue],
  );

  const onPasteStart = useCallback(
    (event: PasteStartEvent<SampleSheetRowDraft>) => {
      const api = event.api;
      const priorClipboardRowCount = pasteContextRef.current.clipboardRowCount;
      const ranges = api.getCellRanges() ?? [];
      const affectedRowIds = new Set<string>();
      const affectedRowIndexByRowId = new Map<string, number>();
      const affectedColumnsById = new Map<string, Column>();

      ranges.forEach((range: CellRange) => {
        const columns = range.columns;
        if (columns.length === 0) {
          return;
        }
        columns.forEach(column => {
          const colId = column.getColId();
          if (colId && !affectedColumnsById.has(colId)) {
            affectedColumnsById.set(colId, column);
          }
        });
        const startRow = range.startRow;
        const endRow = range.endRow ?? range.startRow;
        if (!startRow || !endRow) {
          return;
        }
        if (startRow.rowPinned != null || endRow.rowPinned != null) {
          return;
        }
        const startIndex = Math.min(startRow.rowIndex, endRow.rowIndex);
        const endIndex = Math.max(startRow.rowIndex, endRow.rowIndex);
        for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex += 1) {
          const rowNode = api.getDisplayedRowAtIndex(rowIndex);
          const pastedRowId = rowNode?.data?.rowId;
          if (pastedRowId) {
            affectedRowIds.add(pastedRowId);
            affectedRowIndexByRowId.set(pastedRowId, rowIndex);
          }
        }
      });

      const focusedCell = api.getFocusedCell();
      if (affectedRowIds.size === 0) {
        if (focusedCell && focusedCell.rowPinned == null) {
          const focusedNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
          const focusedRowId = focusedNode?.data?.rowId;
          if (focusedRowId) {
            affectedRowIds.add(focusedRowId);
            affectedRowIndexByRowId.set(focusedRowId, focusedCell.rowIndex);
          }
        }
      }

      if (focusedCell && focusedCell.rowPinned == null) {
        const focusedColumn = focusedCell.column;
        const focusedColId = focusedColumn.getColId();
        if (focusedColId && !affectedColumnsById.has(focusedColId)) {
          affectedColumnsById.set(focusedColId, focusedColumn);
        }
      }

      const beforeRows = new Map<string, SampleSheetRowDraft>();
      affectedRowIds.forEach(rowId => {
        const snapshot = rowsByIdRef.current.get(rowId);
        if (snapshot) {
          beforeRows.set(rowId, cloneRow(snapshot));
        }
      });

      const computeSamplingPointPasteAction = (): BlockedSamplingPointPasteAction => {
        if (typeof beginSubmittedSamplingPointEdit !== 'function') {
          return { kind: 'none' };
        }

        const rowCount = affectedRowIds.size;
        const columns = Array.from(affectedColumnsById.values());
        if (rowCount === 0 || columns.length === 0) {
          return { kind: 'none' };
        }

        const columnFieldIds = columns
          .map(column => resolveGridColumnFieldId(column))
          .filter((fieldId): fieldId is string => typeof fieldId === 'string' && fieldId.trim().length > 0);
        const samplingPointColumnFieldIds = columnFieldIds.filter(fieldId =>
          samplingPointFieldSet.has(fieldId as keyof SampleSheetRowDraft),
        );
        if (samplingPointColumnFieldIds.length === 0) {
          return { kind: 'none' };
        }

        const blockedTargets: Array<{ rowId: string; rowIndex: number; colId: string; field: keyof SampleSheetRowDraft }> = [];
        affectedRowIds.forEach(rowId => {
          const node = api.getRowNode(rowId);
          const rowData = node?.data;
          const rowIndex = affectedRowIndexByRowId.get(rowId) ?? node?.rowIndex;
          if (!node || !rowData || typeof rowIndex !== 'number') {
            return;
          }
          if (!rowData.isSubmittedSample || rowData.isReadOnly) {
            return;
          }
          columns.forEach(column => {
            const fieldId = resolveGridColumnFieldId(column);
            if (!fieldId) {
              return;
            }
            if (!samplingPointFieldSet.has(fieldId as keyof SampleSheetRowDraft)) {
              return;
            }
            if (column.isCellEditable(node)) {
              return;
            }
            blockedTargets.push({
              rowId,
              rowIndex,
              colId: column.getColId(),
              field: fieldId as keyof SampleSheetRowDraft,
            });
          });
        });

        if (blockedTargets.length === 0) {
          return { kind: 'none' };
        }

        const allSelectedColumnsAreSamplingPointFields =
          columnFieldIds.length > 0 &&
          columnFieldIds.every(fieldId => samplingPointFieldSet.has(fieldId as keyof SampleSheetRowDraft));

        const resolvePreferredTarget = () => {
          if (focusedCell && focusedCell.rowPinned == null) {
            const focusedNode = api.getDisplayedRowAtIndex(focusedCell.rowIndex);
            const focusedRowId = focusedNode?.data?.rowId;
            const focusedColId = focusedCell.column.getColId();
            if (focusedRowId && focusedColId) {
              const match = blockedTargets.find(target => target.rowId === focusedRowId && target.colId === focusedColId);
              if (match) {
                return match;
              }
            }
          }
          return blockedTargets[0];
        };

        if (rowCount === 1 && columns.length === 1 && allSelectedColumnsAreSamplingPointFields) {
          return { kind: 'modal', target: resolvePreferredTarget() };
        }

        if (rowCount === 1 && columns.length > 1 && allSelectedColumnsAreSamplingPointFields) {
          return { kind: 'modal', target: resolvePreferredTarget() };
        }

        if (rowCount > 1) {
          return {
            kind: 'toast',
            title: 'Paste not supported for shared sampling points',
            description:
              'This paste includes sampling point fields for submitted samples. Pasting across multiple rows is not supported when the sampling point is shared. Edit one row at a time (double click a sampling point cell to clear first).',
          };
        }

        return {
          kind: 'toast',
          title: 'Paste not supported for shared sampling points',
          description:
            'This paste includes sampling point fields for submitted samples. Multi-cell pastes that include sampling point fields are not supported when the sampling point is shared. Double click a sampling point cell to clear first, then paste again.',
        };
      };

      pasteContextRef.current = {
        blockedCells: 0,
        blockedSamplingPointCells: 0,
        blockedReadOnlyCells: 0,
        affectedRowIds,
        blockedSamplingPointPasteAction: computeSamplingPointPasteAction(),
        clipboardRowCount: priorClipboardRowCount,
        mutatedSamplingFieldsByRow: new Map<string, Set<keyof SampleSheetRowDraft>>(),
        mutatedCells: [],
        pastedCellCount: 0,
        beforeRows,
      };
    },
    [beginSubmittedSamplingPointEdit, rowsByIdRef, samplingPointFieldSet],
  );

  const onPasteEnd = useCallback(
    (_event: PasteEndEvent<SampleSheetRowDraft>) => {
      const context = pasteContextRef.current;
      const api = gridRef.current?.api;
      if (!api) {
        pasteContextRef.current = {
          blockedCells: 0,
          blockedSamplingPointCells: 0,
          blockedReadOnlyCells: 0,
          affectedRowIds: new Set<string>(),
          blockedSamplingPointPasteAction: { kind: 'none' },
          clipboardRowCount: 0,
          mutatedSamplingFieldsByRow: new Map<string, Set<keyof SampleSheetRowDraft>>(),
          mutatedCells: [],
          pastedCellCount: 0,
          beforeRows: new Map<string, SampleSheetRowDraft>(),
        };
        return;
      }

      const manualMode: ResolveMode = {
        kind: 'manualOrSingleField',
        allowByKey: true,
      };
      const multiPasteMode: ResolveMode = {
        kind: 'multiCellPaste',
        allowByKey: false,
      };

      const valueMapByRow = new Map<string, Map<keyof SampleSheetRowDraft, unknown>>();
      context.mutatedCells.forEach(({ rowId, field, value }) => {
        let fieldMap = valueMapByRow.get(rowId);
        if (!fieldMap) {
          fieldMap = new Map<keyof SampleSheetRowDraft, unknown>();
          valueMapByRow.set(rowId, fieldMap);
        }
        fieldMap.set(field, value);
      });

      const ops: QueuePatchOp[] = [];

      valueMapByRow.forEach((fieldMap, rowId) => {
        const rowNodeData = getRowDataById(api, rowId);
        if (!rowNodeData || rowNodeData.isReadOnly) {
          return;
        }

        const beforeRow = context.beforeRows.get(rowId) ?? rowsByIdRef.current.get(rowId) ?? null;
        const baseRow = cloneRow(rowNodeData);
        const mutatedSamplingFields = context.mutatedSamplingFieldsByRow.get(rowId) ?? new Set();
        const isSingleSamplingFieldEdit = mutatedSamplingFields.size === 1 && context.pastedCellCount === 1;
        const primaryMode = isSingleSamplingFieldEdit ? manualMode : multiPasteMode;

        let resolvedRow =
          mutatedSamplingFields.size > 0 ? resolveSamplingPointRef.current(baseRow, primaryMode) : baseRow;

        if (mutatedSamplingFields.size > 0 && primaryMode === multiPasteMode && !resolvedRow.samplingPointId) {
          resolvedRow = resolveSamplingPointRef.current(resolvedRow, manualMode);
        }

        const rowAfterResolution =
          mutatedSamplingFields.size > 0 ? preservePastedFieldValues(resolvedRow, baseRow, fieldMap) : resolvedRow;

        const candidateFields = new Set<keyof SampleSheetRowDraft>(fieldMap.keys());
        if (mutatedSamplingFields.size > 0) {
          mutatedSamplingFields.forEach(field => candidateFields.add(field));
          candidateFields.add('samplingPointId');
          candidateFields.add('sampleDescription');
          candidateFields.add('sampleType');
          candidateFields.add('detailedSampleType');
          candidateFields.add('country');
          candidateFields.add('stateOrRegion');
          candidateFields.add('city');
          candidateFields.add('latitudeLongitude');
        }

        candidateFields.forEach(field => {
          const patchField = toQueuePatchField(field);
          if (!patchField) {
            return;
          }
          const previousValue = beforeRow ? beforeRow[field] : undefined;
          const nextValue = rowAfterResolution[field] ?? null;
          if (!isEqual(previousValue ?? null, nextValue)) {
            ops.push({ rowId, field: patchField, value: nextValue, previousValue });
          }
        });
      });

      if (ops.length > 0) {
        queuePatch(ops);
      }

      const pasteAction =
        context.blockedSamplingPointPasteAction.kind === 'modal' && context.clipboardRowCount > 1
          ? ({
              kind: 'toast',
              title: 'Paste not supported for shared sampling points',
              description:
                'This paste includes sampling point fields for submitted samples. Pasting across multiple rows is not supported when the sampling point is shared. Edit one row at a time (double click a sampling point cell to clear first).',
            } satisfies BlockedSamplingPointPasteAction)
          : context.blockedSamplingPointPasteAction;

      if (pasteAction.kind === 'modal' && typeof beginSubmittedSamplingPointEdit === 'function') {
        void beginSubmittedSamplingPointEdit({
          api,
          rowId: pasteAction.target.rowId,
          rowIndex: pasteAction.target.rowIndex,
          colId: pasteAction.target.colId,
          field: pasteAction.target.field,
        });
      } else if (pasteAction.kind === 'toast') {
        showToast({
          status: 'warning',
          title: pasteAction.title,
          description: pasteAction.description,
        });
      }

      if (context.blockedCells > 0 && pasteAction.kind === 'none') {
        const parts: string[] = [];
        if (context.blockedSamplingPointCells > 0) {
          parts.push(
            `${context.blockedSamplingPointCells} locked sampling-point cell${
              context.blockedSamplingPointCells === 1 ? '' : 's'
            }`,
          );
        }
        if (context.blockedReadOnlyCells > 0) {
          parts.push(`${context.blockedReadOnlyCells} protected cell${context.blockedReadOnlyCells === 1 ? '' : 's'}`);
        }

        const description = parts.join('; ');
        showToast({
          status: context.blockedReadOnlyCells > 0 ? 'error' : 'warning',
          title: 'Paste skipped for protected cells',
          description: `Skipped ${context.blockedCells} cell${context.blockedCells === 1 ? '' : 's'}: ${description}.`,
        });
      }

      pasteContextRef.current = {
        blockedCells: 0,
        blockedSamplingPointCells: 0,
        blockedReadOnlyCells: 0,
        affectedRowIds: new Set<string>(),
        blockedSamplingPointPasteAction: { kind: 'none' },
        clipboardRowCount: 0,
        mutatedSamplingFieldsByRow: new Map<string, Set<keyof SampleSheetRowDraft>>(),
        mutatedCells: [],
        pastedCellCount: 0,
        beforeRows: new Map<string, SampleSheetRowDraft>(),
      };
    },
    [beginSubmittedSamplingPointEdit, gridRef, queuePatch, resolveSamplingPointRef, rowsByIdRef, showToast],
  );

  return {
    processDataFromClipboard,
    processCellForClipboard,
    processCellFromClipboard,
    onPasteStart,
    onPasteEnd,
  } satisfies Pick<
    GridOptions<SampleSheetRowDraft>,
    'processDataFromClipboard' | 'processCellForClipboard' | 'processCellFromClipboard'
  > & {
    onPasteStart: (event: PasteStartEvent<SampleSheetRowDraft>) => void;
    onPasteEnd: (event: PasteEndEvent<SampleSheetRowDraft>) => void;
  };
}
